import React from 'react';
import type { ComponentProps } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Alert, Chip, colors, Grid } from '@itandi/itandi-bb-ui';
import Styles from './Page.module.scss';
import { VendorLayoutV2 } from '~/components/common/layouts/VendorLayoutV2';
import { DealLayout } from '~/components/common/layouts/deals/DealLayout';
import { ConstructionTop } from '~/components/common/constructions/top/ConstructionTop';
import { ConstructionList } from '~/components/common/constructions/list/ConstructionList';
import { PATH } from '~/pages/vendors/deals/[id]';
import { PATH as DEALS_PATH } from '~/pages/vendors/deals';
import { PATH as ESTIMATIONS_EDIT_PATH } from '~/pages/vendors/deals/[id]/estimations/edit';
import { useDeals } from '~/hooks/vendors/useDeals';
import Loading from '~/components/common/parts/loading/Loading';
import { useDealEstimations } from '~/hooks/vendors/deals/useDealEstimations';
import { getDownloadPath } from '~/utils/api/vendors/deals/DealEstimationFile';
import { getDownloadPath as getHistoryDownloadPath } from '~/utils/api/vendors/deals/DealEstimationHistoryFile';
import { useIdQueryHandler } from '~/hooks/useIdQueryHandler';
import { ConstructionConfirmedFooter } from '~/components/common/constructions/ConstructionConfirmedFooter';
import { isOriginVendor } from '~/models/DealVendorCompany';
import { useSessions } from '~/hooks/vendors/useSessions';
import {
  updateAccept,
  updateReject
} from '~/utils/api/vendors/deals/DealEstimationHeader';
import type { NskResponse } from '~/models/Api';
import { useDealEstimationHeaders } from '~/hooks/vendors/deals/useDealEstimationHeaders';
import { IN_ESTIMATION, isOwnStatus } from '~/models/Status';
import { estimationRequestSide } from '~/models/DealEstimationHeader';
import { estimationHeaderStatus, ESTIMATION_BY } from '~/models/Deal';
import { MEDIA_QUERY, useMediaQuery } from '~/hooks/useMediaQuery';

export const Page: React.FC = () => {
  const { match: isSp, isLoaded: windowLoaded } = useMediaQuery(MEDIA_QUERY.sp);
  const id = useIdQueryHandler();
  const router = useRouter();
  const { data: deal, mutate: mutateDeal } = useDeals().show(id);
  const { data: session } = useSessions().index();
  const { data: estimationContext, mutate: mutateEstimations } =
    useDealEstimations().index(id);
  const { data: dealEstimationHeaders, mutate: mutateHeaders } =
    useDealEstimationHeaders().index(id);
  const requestHeader =
    dealEstimationHeaders?.find((header) => header.status === 'request') ??
    null;
  const { data: estimationHistoryContext, isLoading } =
    useDealEstimationHeaders().show(id, requestHeader && requestHeader.id);
  React.useEffect(() => {
    if (deal === null) return;
    if (estimationRequestSide(deal, 'vendor')) {
      router.replace(ESTIMATIONS_EDIT_PATH(deal.id)).then();
    }
  }, [deal, router]);
  if (
    deal === null ||
    isLoading ||
    estimationContext === null ||
    session === null ||
    dealEstimationHeaders === null
  ) {
    return <Loading isLoading />;
  }
  const items = [
    { label: '案件一覧', href: DEALS_PATH, as: Link },
    { label: '案件詳細', href: PATH(deal.id), as: Link },
    { label: '見積り確認' }
  ] as const satisfies ComponentProps<typeof DealLayout>['items'];
  const handleClickPreview = (): void => {
    const path = getDownloadPath(deal.id);
    window.open(path);
  };
  const handleClickHistoryPreview = (): void => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    const path = getHistoryDownloadPath(deal.id, requestHeader.id);
    window.open(path);
  };
  const handleMutateConstructions = async (): Promise<void> => {
    await mutateEstimations?.();
    await mutateHeaders?.();
    await mutateDeal?.();
  };
  const handleAcceptUpdateStatus = async (): Promise<NskResponse<void>> => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    return updateAccept(deal.id, requestHeader.id);
  };
  const handleUpdateRejectStatus = async (): Promise<NskResponse<void>> => {
    if (requestHeader === null) {
      throw new Error('requestHeader cannot be null');
    }
    return updateReject(deal.id, requestHeader.id);
  };
  if (!windowLoaded) return null;
  return (
    <VendorLayoutV2>
      <DealLayout deal={deal} isVendor items={items}>
        <div className={Styles.EstimateHeader}>
          <h1 className={Styles.EstimateTitle}>見積り確認</h1>
          {estimationHeaderStatus(deal).label !== '' && (
            <Chip
              background={estimationHeaderStatus(deal).color}
              borderColor={null}
              label={estimationHeaderStatus(deal).label}
              maxWidth="100%"
              textColor={colors['itandi-white']}
            />
          )}
        </div>
        {estimationHeaderStatus(deal).description !== '' &&
          deal.statusId !== IN_ESTIMATION && (
            <Grid paddingX={isSp ? 16 : 0} width={isSp ? '100%' : '50%'}>
              <Alert>{estimationHeaderStatus(deal).description}</Alert>
            </Grid>
          )}
        <ConstructionTop
          constructionContext={estimationContext}
          dealEstimationHistoryContext={
            estimationHistoryContext ?? estimationHistoryContext
          }
          handleClickEstimationHistoryPreview={
            requestHeader ? handleClickHistoryPreview : null
          }
          handleClickEstimationPreview={handleClickPreview}
          readonly
        />
        <div className={Styles.EstimateList}>
          <ConstructionList
            companyType="vendor"
            constructionContext={estimationHistoryContext ?? estimationContext}
            deal={deal}
            dealConstructionItems={[]}
            docType="estimation"
            handleMutateConstructions={handleMutateConstructions}
            readonly
            showEstimationConfirmedButton={
              (requestHeader &&
                !isOwnStatus(deal.statusId) &&
                deal.estimationBy === ESTIMATION_BY.COMPANY &&
                isOriginVendor(deal, session.vendorCompany.id)) ??
              false
            }
            showHighlight
          />
          {requestHeader && (
            <ConstructionConfirmedFooter
              companyType={
                isOriginVendor(deal, session.vendorCompany.id)
                  ? 'vendor'
                  : 'partner'
              }
              deal={deal}
              dealEstimationHeader={requestHeader}
              handleMutate={handleMutateConstructions}
              handleUpdateAcceptStatus={handleAcceptUpdateStatus}
              handleUpdateRejectStatus={handleUpdateRejectStatus}
              isAdmin={session.admin}
            />
          )}
        </div>
      </DealLayout>
    </VendorLayoutV2>
  );
};
